import React, { useEffect, useState } from 'react';
import {
  FormRenderer,
  componentTypes,
  validatorTypes,
} from '@data-driven-forms/react-form-renderer';
import { componentMapper, FormTemplate } from '@data-driven-forms/ant-component-mapper';
import '../../../../styles/components/MarketPlace/AdditionalInformationForm.scss';
import { useHistory, useLocation } from 'react-router-dom';
import { notification, PageHeader, Spin } from 'antd';
import moment from 'moment';
import {
  updateMarketplaceUserDetails,
  getJobApplicationStatusByApplicationId,
  getZomatoTshirtSize,
  fetchLocalitiesBySiId
} from '../../../../services/API/MarketplaceService';
import { Routes, stacks } from '../../../../constants/RouterConstants';
import { Navigation } from '../../../../services/NavigationService';
import companiesSchemaMap from '../../../../constants/MarketplaceAdditionalFormSchemas/CompaniesSchemaMap';
import { useMitraStore } from '../../../../contexts/MitraContext';
import { useTranslation } from 'react-i18next';
import { logFirebaseEvent } from '../../../../Firebase';

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

function AdditionalInformationForm() {
  const query = useQuery();
  const siId = query.get('siId');
  const companyName = query.get('companyName');
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [formSchema, setFormSchema] = useState({});
  const { selectedMitraDetails } = useMitraStore();
  const {t, i18n} = useTranslation();

  const localizeFormData = (form) => {
    for(let i=0;i<form.fields.length; i++) {
      // changing the label
      console.log(t(`personal_info_${form.fields[i].name}`), form.fields[i].name);
      form.fields[i].label = t(`personal_info_${form.fields[i].name}`) || form.fields[i].label;

      // changing the placeholder
      if (form.fields[i].placeholder) {
        form.fields[i].placeholder = t(`personal_info_placeholder_${form.fields[i].name}`) || form.fields[i].placeholder;
      }

      // changing the options only for gender currently
      if (form.fields[i].options && form.fields[i].name==="gender") {
        for(let j=0;j<form.fields[i].options.length;j++) {
          form.fields[i].options[j].label = t(`personal_info_${form.fields[i].options[j].value}`) || form.fields[i].options[j].label;
        }
      }
    }
  };

  const submit = data => {
    const requestPayload = {
      userId: localStorage.getItem('userId'),
      scheduledInterviewId: siId,
      dataToUpdate: data,
    };

    if (data.dateOfBirth) {
      // Age must be greater than 18
      const DOB = moment(data.dateOfBirth).format('MM/DD/YYYY');
      const currentAge = moment().diff(DOB, 'years');
      if (currentAge < 18) {
        notification.error({
          message: 'Age must be greater than 16.',
        });
        return;
      }
    }

    if (data.dlExpiry) {
      // Licence expiry must be greater than current day's date
      const differenceFromToday = moment(data.dlExpiry).diff(moment(), 'days');
      if (differenceFromToday <= 0) {
        // if licence is already expired
        notification.error({
          message: 'Driving licence must not be expired.',
        });
        return;
      }
    }

    updateMarketplaceUserDetails(requestPayload)
      .then(response => {
        
        logFirebaseEvent('jse_personal_info_filled', {
          userId: localStorage.getItem('userId'),
          timeStamp: moment().format('MM/DD/YYYY HH:mm:ss')
         });
        history.replace(`/application-status?applicationId=${siId}`);
      })
      .catch(error => {
        console.log(error);
        notification.error({
          message: t('something_went_wrong'),
        });
      });
  };

  const setDelhiveryLocalities = (fieldMap) => {
    const payload = {
      siId
    };

    fetchLocalitiesBySiId(payload)
    .then(response => {
      const jobLocalities = response.data.jobLocations;
      console.log(jobLocalities);
      const options = [];
      for (let i=0;i<jobLocalities.length; i++) {
        const label = jobLocalities[i]; 
        const value = jobLocalities[i];
        options.push({label, value});
      }

      // setting the options to the form
      for (let j=0;j<fieldMap.fields.length; j++) {
        if (fieldMap.fields[j].name === 'delhivery3plCompanyHubPreference') {
          fieldMap.fields[j].options = [...options];
        }
      }
      localizeFormData(fieldMap);
      setFormSchema(fieldMap);

    })
    .catch(error => console.log(error))
  }

  const setZomatoTshirtSizeOptions = (fieldMap) => {
    const phoneNumber = selectedMitraDetails.userPhoneNumber;

    getZomatoTshirtSize(phoneNumber)
    .then(response => {
      const shirtSizes = response.data.data.shirts;

      if (!shirtSizes) {
        localizeFormData(fieldMap);
        // setFormSchema(fieldMap);
        return;
      }

      // forming option for sizes
      const options = [];
      for (let i=0;i<shirtSizes.length; i++) {
        const label = shirtSizes[i].size + ' (' + shirtSizes[i].size_in_inch + ')'; 
        const value = shirtSizes[i].size;

        options.push({label, value});
      }

      // setting the options to the form
      for (let j=0;j<fieldMap.fields.length; j++) {
        if (fieldMap.fields[j].name === 'zomatoTShirtSize') {
          fieldMap.fields[j].options = [...options];
        }
      }
      localizeFormData(fieldMap);
      setFormSchema(fieldMap);
    })
    .catch(err => {
      localizeFormData(fieldMap);
      setFormSchema(fieldMap)
    })
  };

  useEffect(() => {
    setLoading(true);
    getJobApplicationStatusByApplicationId(siId)
      .then(response => {
        if (response?.status === 200) {
          return response?.data;
        }
      })
      .then(responseObj => {
        const initialValueMap = new Map();
        for (const data of responseObj?.timelineDetails) {
          if (data?.header === 'Personal Info') {
            for (const field of data?.value?.fields) {
              const initialValueArr = Array.isArray(Object.entries(field)) && Object.entries(field).length > 0
                ? Object.entries(field)[0]
                : null;
              if (
                initialValueArr &&
                Array.isArray(initialValueArr) &&
                initialValueArr.length >= 2
              ) {
                initialValueMap.set(initialValueArr[0], initialValueArr[1]);
              }
            }
          }
        }

        const fieldMap = companiesSchemaMap[companyName.toUpperCase()];

        for (const data of fieldMap?.fields) {
          data.initialValue = initialValueMap.has(data?.name)
            ? initialValueMap.get(data?.name)
            : null;

          if (data?.component === 'date-picker' && moment(data?.initialValue).isValid()) {
            data.initialValue = moment(data?.initialValue, 'YYYY-MM-DD');
          }
        }

        // setting up zomato tshirt size options from API
        if (companyName.toUpperCase() === 'ZOMATO') {
          setZomatoTshirtSizeOptions(fieldMap);
        } else if (companyName.toUpperCase() === 'DELHIVERY_3PL') {
          setDelhiveryLocalities(fieldMap);
        } else {
          localizeFormData(fieldMap);
          setFormSchema(fieldMap);
        }
        setLoading(false);
      })
      .catch(error => {
        console.log(error);
        setLoading(false);
      });
  }, []);

  return (
    <div>
      <PageHeader
        onBack={() => history.replace(`/application-status?applicationId=${siId}`)}
        className="site-page-header"
        title={t('additional_information')}
      />

      <div className="form-container" style={{ margin: '24px' }}>
        {formSchema.fields && !loading && (
          <FormRenderer
            schema={formSchema}
            componentMapper={componentMapper}
            FormTemplate={FormTemplate}
            onSubmit={submit}
          />
        )}
        {loading && <Spin spinning={loading} className="spinner" tip="Loading" />}
      </div>
    </div>
  );
}

export default AdditionalInformationForm;
